<script setup lang="tsx">
import { onBeforeMount, onBeforeUnmount, ref, watch } from 'vue'
import { commonProps } from './props'
import ClearButton from './ClearButton.vue'
import Multiselect from 'vue-multiselect'
import type { Response, Asset } from '@/models/api-generated'
import http from '@/services/http'
import type { Column } from '@/stores/column'
import { useI18n } from 'vue-i18n'
import { hasPermission } from '@/services/utils'
import { PermAllAssetRead, PermOwnAssetRead } from '@/models/enums-generated'
import type { DefaultFilter } from '@/models/filters-generated'

const i18n = useI18n()
const internalModel = ref<Asset[]>([])
const isLoading = ref(false)
const isOpen = ref(false)
const model = defineModel<Column>({ required: true })
const props = defineProps(commonProps)
const options = ref<Asset[]>([])

watch(internalModel, () => (model.value.filter = internalModel.value.map((x) => x.id)))
onBeforeUnmount(() => (model.value.filter = []))
onBeforeMount(() => search(''))

async function search(q: string) {
	isLoading.value = true
	if (hasPermission(PermAllAssetRead) || hasPermission(PermOwnAssetRead)) {
		const flt: DefaultFilter = {
			q,
			excludeIds: [],
			instanceId: 0,
			type: '',
		}

		const { data } = await http.get<Response<Asset[]>>('api/asset/autocomplete', {
			params: flt,
		})

		// @ts-ignore
		data.data?.push({
			id: 0,
			name: i18n.t('allBusinessProcesses'),
		})

		options.value = data.data ?? []
	}

	isLoading.value = false
}
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:id="props.id"
		:disabled="props.disabled"
		:placeholder="model.label + '...'"
		:options="options"
		:internal-search="false"
		:preserve-search="true"
		:show-no-results="false"
		:loading="isLoading"
		v-model="internalModel"
		label="name"
		track-by="id"
		@search-change="search"
		@open="isOpen = true"
		@close="isOpen = false"
		:limit="isOpen ? 100 : 3"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []"></ClearButton>
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>
	</Multiselect>
</template>
